<script lang="ts">
export type GatLinkType =
  | 'phone'
  | 'email'
  | 'link'
  | 'external-link'
  | 'address'
  | 'none'
</script>
<script setup lang="ts">
/**
 * GatLink Component
 *
 * A flexible link component that supports different link types with automatic
 * icon selection and styling. Supports both external URLs and internal routing.
 *
 * @slot default - Customizable content with access to computed icon and text
 * @slot-param {IconValues | undefined} icon - The computed icon for the link
 * @slot-param {string | undefined} text - The text prop passed to the component
 */

import { icons, type IconValues } from '@gat-ship/icons'
import type { GatRouteLocation, AppRoute } from '@gat-ship/lib/router'
import { useRouter } from '@gat-ship/lib/router'
import type {
  BaseActionDropdownItem,
  BaseActionWithClick
} from '@gat-ship/types'
import { globalUtils } from '@gat-ship/utils'
import { computed, type HTMLAttributes } from 'vue'
import { cn } from '../../lib/utils'
import GatIcon, { type GatIconProps } from '../gat-icon/gat-icon.vue'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel
} from '../ui/dropdown-menu'
import GatLinkTrigger from './gat-link-trigger.vue'

/**
 * Valid target values for anchor elements
 */
export type GatLinkTarget = '_blank' | '_self' | '_parent' | '_top'

/**
 * Props interface for the GatLink component
 */
export interface GatLinkProps {
  /**
   * The destination URL or route location for the link.
   * Can be a string URL or a GatRouteLocation object for internal routing.
   */
  href?: string | GatRouteLocation<AppRoute>

  /**
   * The type of link which determines default icon and behavior.
   * - 'phone': Creates a phone link (tel:) with phone icon
   * - 'email': Creates an email link (mailto:) with mail icon
   * - 'link': Creates a standard link with link icon
   * - 'external-link': Creates an external link that opens in new tab with external link icon
   */
  type: GatLinkType

  /** Optional text content to display in the link */
  text?: string

  /** Additional CSS classes to apply to the link element */
  class?: HTMLAttributes['class']

  /**
   * Target attribute for the anchor element.
   * Note: 'external-link' type will automatically set target to '_blank'
   */
  target?: GatLinkTarget

  /**
   * Icon to display with the link.
   * - Pass an IconValues to show a specific icon
   * - Pass 'none' to hide the icon completely
   * - If not provided, icon will be determined by the link type
   */
  icon?: IconValues | 'none'

  /**
   * The position of the icon.
   * - 'left': The icon will be displayed on the left side of the text
   * - 'right': The icon will be displayed on the right side of the text
   */
  iconPosition?: 'left' | 'right'

  iconProps?: Partial<GatIconProps>

  /**
   * Whether to display the link as disabled.
   */
  disabled?: boolean

  /**
   * Whether to display the link as disabled.
   */
  disabledText?: string

  /**
   * Additional dropdown items to display in the link.
   */
  dropdownItems?: (BaseActionDropdownItem | undefined)[]

  /** The data-testid attribute */
  dataTestid?: string
}

const props = withDefaults(defineProps<GatLinkProps>(), {
  iconPosition: undefined,
  dropdownItems: () => []
})

const router = useRouter()

function getPrefix(type: GatLinkProps['type']) {
  return type === 'phone' ? 'tel:' : type === 'email' ? 'mailto:' : ''
}

function getHref(
  type: GatLinkProps['type'],
  href: GatLinkProps['href'],
  withPrefix: boolean
) {
  if (!href) {
    return undefined
  }
  const prefix = getPrefix(type)
  if (typeof href === 'string') {
    return withPrefix ? `${prefix}${href}` : href
  }
  return withPrefix
    ? `${prefix}${router.resolve(href).path}`
    : router.resolve(href).path
}

/**
 * Computed property that resolves the href prop to a string URL.
 * If href is a string, returns it as-is.
 * If href is a route location object, resolves it using the router.
 */
const _href = computed(() => {
  return getHref(props.type, props.href, true)
})

/**
 * Computed property that determines which icon to display.
 * Priority order:
 * 1. Explicit icon prop (if not 'none')
 * 2. Default icon based on link type
 * 3. No icon if type doesn't have a default or icon is 'none'
 */
const _icon = computed((): IconValues | undefined => {
  if (props.icon === 'none') {
    return undefined
  }
  if (props.icon) {
    return props.icon
  }
  if (props.type === 'phone') {
    return icons.common.Phone
  }
  if (props.type === 'email') {
    return icons.common.Mail
  }
  if (props.type === 'link') {
    return icons.common.Link
  }
  if (props.type === 'external-link') {
    return icons.common.ExternalLink
  }
  if (props.type === 'address') {
    return icons.common.MapPinned
  }
  if (props.type === 'none') {
    return undefined
  }
  return undefined
})

const _dropdownItems = computed(() => {
  const { href, dropdownItems, type } = props
  if (type === 'link') {
    return []
  }

  const result: (BaseActionDropdownItem & { as?: 'a'; href?: string })[] = []
  if (
    (type === 'phone' || type === 'email') &&
    href &&
    typeof href === 'string'
  ) {
    result.push({
      label: 'Copy',
      icon: icons.common.Copy,
      onClick: () => {
        globalUtils.functions.copyToClipboard(href)
      }
    })
  }

  if (type === 'external-link' && href) {
    result.push({
      as: 'a',
      href: _href.value,
      label: 'Open in new tab',
      icon: icons.common.ExternalLink,
      onClick: () => {
        if (typeof props.href === 'string') {
          window.open(props.href, '_blank')
        } else if (props.href) {
          router.openInNewTab(props.href)
        }
      }
    })
  }
  if (type === 'email' && href) {
    result.push({
      as: 'a',
      href: _href.value,
      label: 'Send email',
      icon: icons.common.Mail,
      onClick: () => { }
    })
  }
  if (type === 'phone' && href) {
    result.push({
      as: 'a',
      href: _href.value,
      label: 'Call phone',
      icon: icons.common.Phone,
      onClick: () => { }
    })
  }

  dropdownItems
    .filter((item) => item !== undefined)
    .forEach((item, index) => {
      result.push({
        ...item,
        separatorBefore: index === 0 && result.length > 0
      })
    })
  return result
})
/**
 * Computed property that determines the target attribute.
 * External links automatically open in a new tab (_blank).
 * Other link types use the explicitly provided target or no target.
 */
const _target = computed(() => {
  if (props.type === 'external-link') {
    return '_blank'
  }
  return props.target
})

const _text = computed(() => {
  if (props.disabled && props.disabledText) {
    return props.disabledText
  }
  const value = props.text ?? getHref(props.type, props.href, false)
  if (props.type === 'phone') {
    if (props.disabled || !value) {
      return 'No phone number'
    }
    return globalUtils.functions.formatString(value, 'phone-number')
  }
  if (props.type === 'email') {
    if (props.disabled) {
      return 'No email'
    }
    return value
  }
  return value
})

const _iconPosition = computed(() => {
  if (props.iconPosition) {
    return props.iconPosition
  }
  if (props.type === 'external-link' || props.type === 'link') {
    return 'right'
  }
  return props.iconPosition ?? 'left'
})
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child :disabled="_dropdownItems.length === 0">
      <GatLinkTrigger :disabled="props.disabled" :data-testid="props.dataTestid" :class="props.class"
        :icon="_icon ?? undefined" :icon-position="_iconPosition" :icon-props="props.iconProps" :text="_text"
        :dropdown-items="_dropdownItems" :type="props.type" :href="_href">
        <slot v-bind="{ icon: _icon, text: _text }" />
      </GatLinkTrigger>
      <!-- <div
          :data-disabled="props.disabled"
          :data-testid="props.dataTestid"
          :class="
            cn(
              'data-[disabled=true]:text-soft data-[disabled=true]:hover:text-link flex items-center gap-1',
              {
                'hover:text-link hover:decoration-link hover:cursor-pointer hover:underline':
                  _dropdownItems.length > 0
              },
              props.class
            )
          "
        >
          <slot v-bind="{ icon: _icon, text: _text }">
            <span v-if="_icon && _iconPosition === 'left'" class="flex items-center justify-center">
              <GatIcon :icon="_icon" v-bind="iconProps" :class="cn('size-3', iconProps?.class)" />
            </span>
            <span class="truncate">{{ _text }}</span>
            <span
              v-if="_icon && _iconPosition === 'right'"
              class="flex items-center justify-center"
            >
              <GatIcon :icon="_icon" v-bind="iconProps" :class="cn('size-3', iconProps?.class)" />
            </span>
          </slot>
        </div> -->
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start">
      <template v-for="item in _dropdownItems" :key="item.label">
        <DropdownMenuSeparator v-if="item.separatorBefore" />
        <DropdownMenuLabel v-if="item.title" :class="item.class" :disabled="item.disabled">
          <span class="truncate">{{ item.title }}</span>
        </DropdownMenuLabel>
        <DropdownMenuItem v-else :as="item.as" :href="item.href" :target="_target" :class="item.class"
          :disabled="item.disabled" @click="item.onClick">
          <GatIcon v-if="item.icon" :icon="item.icon" class="size-3" />
          <span class="truncate">{{ item.label }}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator v-if="item.separatorAfter" />
      </template>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
